import { useContext } from 'react'
import { useNavigate } from 'react-router-dom'

import { useArticleDetailApi } from '@/common/api/modules/articles'
import { useRequestAutoArticleCreationMutation } from '@/common/api/modules/mediaAction'
import { priorityLabel, priorityTextColor } from '@/common/app/modules/media/articleCreator'
import {
  ActionTabKey,
  actionTypeLabel,
  buildActionTargetUrl,
  sourceAnalysisLabel
} from '@/common/app/modules/media/mediaAction'
import { articleDetailPath, mediaAnalysisResultPath } from '@/common/app/path'
import { dateStr } from '@/common/form/date'
import { MediaAction, MediaActionStatus } from '@/common/model/media/mediaAction'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import AiButton from '@/components/view/atoms/button/AiButton'
import TextButton from '@/components/view/atoms/button/TextButton'
import CheckBox from '@/components/view/atoms/checkbox/CheckBox'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import InsideArea, { BgColor } from '@/components/view/layouts/area/InsideArea'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'

import { ActionDetailModalContext } from '@/components/container/modal/automationReview/ActionDetailModalProvider'
import { ConfirmDialogContext } from '@/components/container/modal/dialog/confirm/ConfirmDialogProvider'
import MediaActionStatusDislosureSelector from '@/components/container/modules/media/action/MediaActionStatusDisclosureSelector'

import MediaActionDecomposeButton from './MediaActionDecomposeButton'

interface CreatedArticleLinkProps {
  siteId: number
  articleProposalId: number
  metaTitle?: string
  metaVersion?: number
}

// 自動作成された記事へのリンク＋タイトル・バージョン表示。
// auto_draft_meta に無い場合（旧レコード）は記事詳細APIから取得する
const CreatedArticleLink = ({
  siteId,
  articleProposalId,
  metaTitle,
  metaVersion
}: CreatedArticleLinkProps) => {
  const hasMeta = metaTitle !== undefined && metaVersion !== undefined
  const { data } = useArticleDetailApi(
    hasMeta ? null : { site_id: siteId, article_proposal_id: articleProposalId }
  )
  const title = metaTitle ?? data?.latest_article.title ?? undefined
  const version = metaVersion ?? data?.latest_article.version_number ?? undefined

  return (
    <InsideArea color={BgColor.SECONDARY} type='thin'>
      <ItemAlign nowrap>
        <TextButton size={12} link={articleDetailPath(siteId, articleProposalId)} newTab truncate>
          {title ?? '作成された記事を見る'}
        </TextButton>
        <Text size={12} nowrap>
          {version ? `v${version}` : ''}
        </Text>
      </ItemAlign>
    </InsideArea>
  )
}

interface Props {
  siteId: number
  action: MediaAction
  tab: ActionTabKey
  selected: boolean
  onToggleSelect(): void
  onUpdateStatus(id: number, status: MediaActionStatus, extra?: { rejection_note?: string }): void
  onChanged(): void
}

// 推奨アクション一覧の行
const MediaActionListRow = ({
  siteId,
  action: a,
  tab,
  selected,
  onToggleSelect,
  onUpdateStatus,
  onChanged
}: Props) => {
  const navigate = useNavigate()

  const { showModal: showConfirm, hide: hideConfirm } = useContext(ConfirmDialogContext)
  const { showModal: showDetail } = useContext(ActionDetailModalContext)

  const { trigger: requestAutoArticleCreation, isMutating: requestingAutoCreate } =
    useRequestAutoArticleCreationMutation({
      onSuccess: () => {
        hideConfirm()
        onChanged()
      }
    })

  // AI記事作成／記事リライト：対象画面へ遷移（提案中の場合は進行中へも遷移する）
  const handleOpenTargetScreen = () => {
    if (tab === 'suggested') {
      onUpdateStatus(a.id, 'in_progress')
    }
    const url = buildActionTargetUrl(a, siteId)
    if (url) navigate(url)
  }

  const handleAutoCreate = () =>
    showConfirm({
      message:
        'この推奨アクションから記事の自動作成を予約します。バックグラウンドで数分以内に処理され、他の操作は完了までできなくなります。よろしいですか？',
      noText: 'キャンセル',
      yesText: '自動記事作成を予約',
      onClickYes: async () => {
        await requestAutoArticleCreation({ site_id: siteId, action_id: a.id })
      }
    })

  const createdArticleProposalId = a.auto_draft_meta?.created_article_proposal_id

  return (
    <TableRow>
      <TableRowItem checkbox width={40}>
        {tab === 'suggested' && (
          <CheckBox name={`select-action-${a.id}`} checked={selected} onChange={onToggleSelect} />
        )}
      </TableRowItem>
      <TableRowItem width={88}>
        <TextButton onClick={() => showDetail({ siteId, actionId: a.id, onChanged })}>
          #{a.id}
        </TextButton>
      </TableRowItem>
      <TableRowItem width={40}>
        <Text
          size={14}
          weight={a.priority == 'low' ? 'normal' : 'bold'}
          color={priorityTextColor(a.priority)}
        >
          {priorityLabel(a.priority)}
        </Text>
      </TableRowItem>
      <TableRowItem width={128} innerGap={8}>
        <Badge size={12} badgeType={BadgeType.SQUARE_PRIMARY}>
          {actionTypeLabel(a.action_type)}
        </Badge>
        {a.source_analysis_type && a.source_history_id ? (
          <TextButton
            newTab
            size={12}
            link={mediaAnalysisResultPath(siteId, a.source_analysis_type, a.source_history_id)}
          >
            {sourceAnalysisLabel(a.source_analysis_type)}
          </TextButton>
        ) : (
          <Text>-</Text>
        )}
      </TableRowItem>
      <TableRowItem innerGap={4} width={420}>
        <Text size={14} weight='bold'>
          {a.title}
        </Text>
        {a.description && (
          <Text size={12} color={TextColor.SECONDARY}>
            {a.description}
          </Text>
        )}
        {a.rejection_note && tab === 'rejected' && (
          <Text size={12} color={TextColor.CRITICAL}>
            却下メモ: {a.rejection_note}
          </Text>
        )}
        {createdArticleProposalId && (
          <CreatedArticleLink
            siteId={siteId}
            articleProposalId={createdArticleProposalId}
            metaTitle={a.auto_draft_meta?.created_article_title}
            metaVersion={a.auto_draft_meta?.created_article_version}
          />
        )}
      </TableRowItem>
      <TableRowItem width={160}>
        {(a.target_keywords ?? []).length > 0 ? (
          <div className='flex flex-wrap gap-1'>
            {a.target_keywords.slice(0, 4).map((kw, i) => (
              <Badge key={i} size={10} badgeType={BadgeType.KEYWORD}>
                {kw}
              </Badge>
            ))}
            {a.target_keywords.length > 4 && (
              <Text size={10} color={TextColor.SECONDARY}>
                +{a.target_keywords.length - 4}
              </Text>
            )}
          </div>
        ) : (
          <Text size={12} color={TextColor.SECONDARY}>
            -
          </Text>
        )}
      </TableRowItem>
      <TableRowItem width={128}>
        <MediaActionStatusDislosureSelector
          siteId={siteId}
          actionId={a.id}
          status={a.status as MediaActionStatus}
          disabled={a.auto_article_creation_requested}
          onUpdated={onChanged}
        />
      </TableRowItem>
      <TableRowItem width={128}>
        <Text size={14}>{a.suggested_at ? dateStr(a.suggested_at) : '-'}</Text>
      </TableRowItem>
      <TableRowItem width={200} innerGap={8}>
        {a.auto_article_creation_requested ? (
          <AiButton loading>自動実行中</AiButton>
        ) : (
          (tab === 'suggested' || tab === 'in_progress') && (
            <>
              {a.action_type === 'create_article' && (
                <>
                  <div>
                    <AiButton onClick={handleAutoCreate} loading={requestingAutoCreate}>
                      自動記事作成
                    </AiButton>
                  </div>
                  <TextButton onClick={handleOpenTargetScreen} arrow>
                    AI記事作成
                  </TextButton>
                </>
              )}
              {a.action_type === 'rewrite_article' && (
                <TextButton onClick={handleOpenTargetScreen} arrow>
                  記事リライト
                </TextButton>
              )}
              {tab === 'suggested' && a.action_type === 'strategic_action' && (
                <MediaActionDecomposeButton siteId={siteId} action={a} onChanged={onChanged} />
              )}
            </>
          )
        )}
      </TableRowItem>
    </TableRow>
  )
}

export default MediaActionListRow
